import TickSquare from "@assets/icons/tick-square.svg";
import Warning from "@assets/icons/warning-filled.svg";
import { Block } from "@components/block";
import { Button, Link } from "@components/button";
import { FilterConfig } from "@components/filter-modal";
import { Modal } from "@components/modal";
import { SelectOption } from "@components/select";
import { StatusBlock, StatusBlockType } from "@components/status-block";
import { Config, DoubleText, Table } from "@components/table";
import { formatDate, formatTime } from "@helpers/date";
import { formatRubles, formatUsdt } from "@helpers/format";
import { usePagination } from "@hooks/use-pagination";
import { useUrlFilters } from "@hooks/useUrlFilters";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Order, OrderForm } from "../api";
import { useOrders, useOrdersDispatch } from "../index";
import styles from "./transactions.module.scss";
import { useDevices } from "@features/device";
import { TraderDetailedOrderModal } from "@components/traderDetailedOrderModal";
import { useBoolState } from "@hooks/use-bool-state";
import { EllipsisText } from "@components/ellipsis-text";
import Arrow from "@assets/icons/arrow-right.svg";
import { OrderCSVModal } from "@components/order-csv/OrderCSV";
import { useWebSocket } from "@request/useWebsocket";
import { WebsocketUpdateType } from "@request/websocket";

export const Transactions = () => {
  const [selectedOrderId, setSelectedOrderId] = useState<string>();
  const [detailedOrder, setDetailedOrder] = useState<Order | null>(null);
  const [isModalOpen, showModal, hideModal] = useBoolState(false);
  const [isCSVModalOpen, showCSVModal, hideCSVModal] = useBoolState(false);

  const resetSelectedOrderId = useCallback(() => {
    setSelectedOrderId(undefined);
  }, []);

  const config: Config<Order> = useMemo(
    () => [
      {
        label: "Статус",
        source: "status",
        renderComponent: (status) => {
          let text = "";
          let statusType: StatusBlockType;
          switch (status) {
            case "canceled":
              text = "Отклонена";
              statusType = StatusBlockType.Error;
              break;
            case "completed":
              text = "Успешна";
              statusType = StatusBlockType.Success;
              break;
            case "dispute_open":
              text = "Открыт спор";
              statusType = StatusBlockType.Warning;
              break;
            case "pending":
            case "processing":
            default:
              text = "Ожидает подтверждения";
              statusType = StatusBlockType.Warning;
              break;
          }
          return <StatusBlock text={text} type={statusType} />;
        },
      },
      {
        label: "Номер заявки",
        source: "id",
        renderComponent: (item) => <EllipsisText text={item} copy />,
      },

      {
        label: "Дата и время",
        source: "created_at_unix",
        renderComponent: (dateTime) => {
          const date = formatDate(new Date(dateTime * 1000));
          const time = formatTime(new Date(dateTime * 1000));

          return <DoubleText text={time} subText={date} />;
        },
      },
      {
        label: "Сумма заявки",
        source: "order_start_amount_usdt",
        renderComponent: (sum, item) => {
          const usdt = formatUsdt(sum);
          const rubles = formatRubles(item.order_start_amount);

          return <DoubleText text={usdt} subText={rubles} />;
        },
      },
      // {
      //   label: "Сумма мерчанта",
      //   source: "merchant_amount",
      //   renderComponent: (data, item) => {
      //     if (item.merchant_amount_usdt == null || data == null) return "";
      //     if (item.merchant_amount_usdt == 0 || data == 0) return "";
      //     const usdt = formatUsdt(item.merchant_amount_usdt);
      //     const rubles = formatRubles(data);

      //     return <DoubleText text={usdt} subText={rubles} />;
      //   },
      // },
      {
        label: "Итоговая сумма",
        source: "order_final_amount",
        renderComponent: (data, item) => {
          if (item.order_final_amount_usdt == null || data == null) return "";
          if (item.order_final_amount_usdt == 0 || data == 0) return "";
          const usdt = formatUsdt(item.order_final_amount_usdt);
          const rubles = formatRubles(data);

          return <DoubleText text={usdt} subText={rubles} />;
        },
      },
      {
        label: "Прибыль",
        source: "ern_usdt",
        renderComponent: (sum, item) => {
          const usdt = formatUsdt(sum);
          const rubles = formatRubles(item.ern);

          return <DoubleText text={usdt} subText={rubles} />;
        },
      },
      { label: "Устройство", source: "device_name" },
      {
        label: "Реквизиты",
        source: "requisite",
        renderComponent: (item) => <EllipsisText text={item} copy />,
      },
      {
        label: "Действие",
        source: "status",
        renderComponent: (status, order) => {
          if (status === "completed") {
            return (
              <div className={styles.action}>
                <TickSquare width={24} height={24} />
              </div>
            );
          }

          return (
            <Link
              onClick={(e) => {
                e.stopPropagation();
                setSelectedOrderId(order.id);
              }}
            >
              Подтвердить
            </Link>
          );
        },
      },
    ],
    []
  );

  const { state, getOrders } = useOrders();
  const { state: devicesState, getDevicesSmall } = useDevices();
  const { page, limit, setPage, setLimit } = usePagination(100);
  const { setParams } = useUrlFilters();
  const [deviceOptions, setDeviceOptions] = useState<SelectOption[]>([]);
  const { lastMessage } = useWebSocket();

  useEffect(() => {
    if (lastMessage && lastMessage.type == WebsocketUpdateType.Order) {
      getOrders({ page, limit });
    }
  }, [lastMessage, page, limit]);

  useEffect(() => {
    const data: Record<string, unknown> = {};
    const url = new URL(window.location.href);
    const { searchParams } = url;
    for (const [key, value] of searchParams.entries()) {
      data[key] = value;
    }
    data.page = page;
    data.limit = limit;
    getOrders(data as OrderForm);
  }, [getOrders, limit, page]);

  useEffect(() => {
    getDevicesSmall();
  }, [getDevicesSmall]);

  useEffect(() => {
    const options = devicesState.devicesSmall.map((deviceSmall) => ({
      text: deviceSmall.name,
      value: deviceSmall.id,
    }));
    setDeviceOptions(options);
  }, [devicesState.devicesSmall]);

  const statusOptions: SelectOption[] = [
    { text: "Успешна", value: "completed" },
    { text: "На рассмотрении", value: "pending" },
    { text: "Отменена", value: "canceled" },
  ];

  const filterConfig: FilterConfig<OrderForm> = [
    {
      type: "select",
      options: statusOptions,
      name: "status",
      label: "Статус заявки",
    },
    {
      type: "sum",
      minName: "min_sum",
      maxName: "max_sum",
    },
    {
      type: "select",
      options: deviceOptions,
      name: "device_id",
      label: "Устройство",
    },
    {
      type: "date",
      withTime: true,
      nameStart: "date_start",
      nameEnd: "date_end",
      returnValue: "number",
    },
  ];
  const onSubmit = (data: OrderForm) => {
    setParams(data);
    data.page = 1;
    data.limit = limit;
    getOrders(data);
  };
    const onSearch = (query: string) => {
    setParams({order_id: query});
    getOrders({order_id: query, limit: 100, page: 1});
  }
  return (
    <>
      <Block>
        <div className={styles.table_header}>
          <h3>История сделок</h3>
          <Button icon={Arrow} onClick={showCSVModal}>
            Создать выгрузку
          </Button>
        </div>
        <Table
          className={styles.table}
          cellClassName={styles.tableCell}
          placeholder="Поиск по сделкам"
          emptyText="Нет сделок"
          withFilter
          filterProps={{
            filterConfig: filterConfig,
            // validationRules: validationRules,
            onSubmit: onSubmit,
          }}
          config={config}
          data={state.orders}
          loading={state.loading}
          setPage={setPage}
          setLimit={setLimit}
          limits={[100, 200, 300]}
          page={page}
          limit={limit}
          totalCount={state.totalCount}
          onRowClick={(item) => {
            setDetailedOrder(item);
            showModal();
          }}
          onSearch={onSearch}
        />
      </Block>
      <TransactionConfirm
        orderId={selectedOrderId}
        hide={resetSelectedOrderId}
      />
      {detailedOrder != null && (
        <TraderDetailedOrderModal
          isOpen={isModalOpen}
          hide={hideModal}
          order={detailedOrder}
        />
      )}
      {isCSVModalOpen && (
        <OrderCSVModal isModalOpen={isCSVModalOpen} hideModal={hideCSVModal} />
      )}
    </>
  );
};

type PayoutConfirmProps = {
  orderId?: string;
  hide: () => void;
};

const TransactionConfirm = ({ hide, orderId }: PayoutConfirmProps) => {
  const { confirmOrder } = useOrdersDispatch();

  const onConfirm = useCallback(() => {
    if (orderId) {
      confirmOrder(orderId);
      hide();
    }
  }, [confirmOrder, hide, orderId]);

  return (
    <Modal
      isOpen={!!orderId}
      onClose={hide}
      className={styles.transactionConfirm}
    >
      <div className={styles.transactionConfirmContainer}>
        <Warning />
        <p className={styles.title}>Вы уверены?</p>
        <p className={styles.subTitle}>Принятие проведения сделки</p>
      </div>
      <div className={styles.buttons}>
        <Button onClick={onConfirm}>Да</Button>
        <Button type="danger" onClick={hide}>
          Нет
        </Button>
      </div>
    </Modal>
  );
};
